/**
 * OrganizationSwitcher - Dropdown to switch between user's organizations
 *
 * Features:
 * - Shows current active organization
 * - Conditional menu options based on user role and organization count
 * - Allows switching between organizations via dialog
 * - Allows managing organization if user is owner or platform admin
 * - Displays organization avatar/logo
 */

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useActiveOrganization, useOrganizations, useUser } from "../../auth/AuthProvider"
import { useFeatureEnabled } from "../../hooks/useFeatureToggles"
import {
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuTrigger,
  MenuSeparator,
  Button,
  HStack,
  Text,
  VStack,
  Avatar,
  Box,
  Icon,
  MenuPositioner,
  Center,
} from "@chakra-ui/react"
import { OrganizationSwitchDialog } from "./OrganizationSwitchDialog"
import { OrgSettingsDialog } from "../../views/OrgSettingsDialog"
import { canAccessOrgSettings } from "./orgAccessControl"
// Helper to capitalize first letter
const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
}
import { FaChevronDown } from "react-icons/fa"

export function OrganizationSwitcher({ showLogo = true }: { showLogo?: boolean }) {
  const { activeOrganization } = useActiveOrganization()
  const { organizations } = useOrganizations()
  const user = useUser()
  const [showSwitchDialog, setShowSwitchDialog] = useState(false)
  const [showOrgSettings, setShowOrgSettings] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const navigate = useNavigate()

  const isPlatformAdmin = user?.role === "admin"
  const isOwner = activeOrganization?.member.role === "owner" || isPlatformAdmin
  const isNonAdminOrgManagementEnabled = useFeatureEnabled("non_admin_org_management")
  const isIntegrationManagementEnabled = useFeatureEnabled("integration_management")

  // Access control: visible when user is platform admin OR org member role is "admin"/"owner"
  const canAccessSettings = canAccessOrgSettings(activeOrganization, user)

  // Organisation Settings is shown only when it has Members to manage. Data Connectors alone are
  // covered by the Connections page, so an org with only integration_management gets just Connections.
  const showOrgSettingsItem = canAccessSettings && isNonAdminOrgManagementEnabled
  const showConnectionsItem = canAccessSettings && isIntegrationManagementEnabled
  const showSettingsItems = showOrgSettingsItem || showConnectionsItem

  // Determine if user can manage organizations (for switch dialog separator logic)
  const canManageOrganization = isOwner && isNonAdminOrgManagementEnabled
  const hasMultipleOrgs = organizations.length > 1
  const hasNoOrganizations = organizations.length === 0
  const hasNoActiveOrg = !activeOrganization && !hasNoOrganizations
  const isMemberWithSingleOrg = !canManageOrganization && !showSettingsItems && !hasMultipleOrgs && activeOrganization

  // If user has organizations but no active one, allow them to select
  // If member with single org and active org is set, don't show menu - just display the org info
  // const shouldShowMenu = hasNoActiveOrg || !isMemberWithSingleOrg

  // Show placeholder for users without an active organization
  const displayName = activeOrganization
    ? activeOrganization.organization.name
    : organizations.length > 0
    ? "Please select organisation"
    : "No organisation"
  const displayLogo = activeOrganization?.organization.logo

  const handleSelectOrg = () => {
    // If no active org but has organizations, show switch dialog
    if (hasNoActiveOrg) {
      setShowSwitchDialog(true)
    }
  }

  const orgButton = (
    <Button
      variant={"highlight" as any}
      size="sm"
      width="fit-content"
      justifyContent="flex-start"
      height="35px !important"
      disabled={(!!isMemberWithSingleOrg && !showSettingsItems) || hasNoOrganizations}
      p={"0 !important"}
      gap="4px"
      onClick={hasNoActiveOrg ? handleSelectOrg : undefined}
      className={isMenuOpen ? "active" : ""}
    >
      <HStack pl={showLogo ? "0" : "10px"} pr={{ base: "0", md: "10px" }} width="fit-content">
        {showLogo && (
          <Box width="35px" height="35px" borderRadius="5px">
            <Center height="100%" width="100%">
              <Avatar.Root shape="square" bg="blue" height="30px" width="30px" borderRadius="5px">
                <Avatar.Image src={displayLogo || undefined} objectFit="contain" />
                <Avatar.Fallback name={capitalizeFirstLetter(displayName).charAt(0)} />
              </Avatar.Root>
            </Center>
          </Box>
        )}

        <HStack display={showLogo ? { base: "none", md: "flex" } : "flex"}>
          <VStack align="flex-start" gap={0} flex={1} minWidth={0}>
            <Text fontSize="16px" fontWeight="semibold" lineHeight="tight" truncate>
              {capitalizeFirstLetter(displayName)}
            </Text>
          </VStack>
          <Box display={showSettingsItems || hasMultipleOrgs ? "block" : "none"}>
            <Icon as={FaChevronDown} size="sm" />
          </Box>
        </HStack>
      </HStack>
    </Button>
  )

  // If member with single org and active org is set, just show the button without menu
  if ((isMemberWithSingleOrg && !showSettingsItems) || hasNoOrganizations) {
    return orgButton
  }

  return (
    <>
      <MenuRoot
        onOpenChange={(open) => {
          if (!hasNoActiveOrg) setIsMenuOpen(open.open)
        }}
        open={isMenuOpen}
      >
        <MenuTrigger asChild>{orgButton}</MenuTrigger>

        <MenuPositioner width="fit-content">
          <MenuContent background="blue" display="flex" flexDirection="column" gap={"3px"} padding="3px">
            {(hasMultipleOrgs || hasNoActiveOrg) && (
              <>
                <MenuItem
                  value="change-org"
                  onClick={() => setShowSwitchDialog(true)}
                  bg={"blue"}
                  color="white"
                  _hover={{ bg: "active", cursor: "pointer" }}
                >
                  <HStack gap={2} width="full">
                    <Text flex={1} fontSize="sm">
                      {hasNoActiveOrg ? "Select Organisation" : "Change Organisation"}
                    </Text>
                  </HStack>
                </MenuItem>
                {showSettingsItems && <MenuSeparator />}
              </>
            )}

            {showOrgSettingsItem && (
              <MenuItem
                value="org-settings"
                onClick={() => setShowOrgSettings(true)}
                bg={"blue"}
                color="white"
                _hover={{ bg: "active", cursor: "pointer" }}
              >
                <HStack gap={2} width="full">
                  <Text flex={1} fontSize="sm">
                    Organisation Settings
                  </Text>
                </HStack>
              </MenuItem>
            )}

            {showConnectionsItem && (
              <MenuItem
                value="connections"
                onClick={() => navigate("/connections")}
                bg={"blue"}
                color="white"
                _hover={{ bg: "active", cursor: "pointer" }}
              >
                <HStack gap={2} width="full">
                  <Text flex={1} fontSize="sm">
                    Connections
                  </Text>
                </HStack>
              </MenuItem>
            )}
          </MenuContent>
        </MenuPositioner>
      </MenuRoot>

      {showSwitchDialog && (
        <OrganizationSwitchDialog open={showSwitchDialog} onClose={() => setShowSwitchDialog(false)} />
      )}

      {showOrgSettings && (
        <OrgSettingsDialog open={showOrgSettings} onClose={() => setShowOrgSettings(false)} />
      )}
    </>
  )
}
export default OrganizationSwitcher
